import { ApolloClient, gql } from '@apollo/client/core';
import { Query } from '../../../generated/graphql';
import * as Order from './Order';
import * as OrderDetail from './OrderDetail';
import { OrderSearchArguments } from './OrderSearchArguments';
import { CONNECTION_METADATA_FIELDS, ConnectionArguments } from '../../connection/Api';
import * as GqlConnection from '../../connection/GqlConnection';
import * as Ordering from '../../../api/Shared/Ordering';
import * as VPError from '../../../VPError';
import { UpdateOrderCustomerInput } from '../../../generated/api.graphql';

import { EVENT_SUMMARY_FRAGMENT } from '../Event/fragments';

const ORDER_SUMMARY_FIELDS = gql`
  fragment OrderSummaryFields on Order {
    id
    createdAt
    updatedAt
    event {
      ...EventSummaryFields
    }
    state
    fingerprint
    transactionId
    total
    currency
    origin
    scannedCount
    ticketCount
    paymentMethod
    cardBrand
    lastFourDigits
    firstName
    lastName
    email
    phoneNumber
    account {
      id
      name
    }
    customer {
      id
      email
      firstName
      lastName
      phoneNumber
    }
  }
  ${EVENT_SUMMARY_FRAGMENT}
`;

const ORDER_DETAIL_FIELDS = gql`
  fragment OrderDetailFields on OrderDetails {
    id
    createdAt
    updatedAt
    # Wider than EventSummaryFields: printed tickets need the venue's locale, currency and
    # address plus the announce promoter/support. Kept local to this fragment rather than
    # widened into EventSummaryFields, which the orders LIST query also uses.
    event {
      ...EventSummaryFields
      venue {
        id
        name
        locale
        currency
        timezone
        address {
          city
          country
          postalCode
          state
          street1
          street2
        }
      }
      announceDetail {
        promoter
        support
      }
    }
    state
    fingerprint
    transactionId
    total
    ticketLayout
    donation
    processingFee
    taxFee
    platformFee
    refundStatus
    currency
    origin
    account {
      id
      name
    }
    firstName
    lastName
    email
    phoneNumber
    customer {
      id
      email
      firstName
      lastName
      phoneNumber
    }
    scannedCount
    ticketCount
    lineItems {
      id
      name
      price
      fee
      paid
      kind
      tier
      scanned
      seat
      table
      token
      firstName
      lastName
      pdfUrl
      pdfPreviewUrl
      claimable
      ticketId
      scans {
        scannedAt
        scannedBy {
          name
          email
        }
      }
    }
    purchasedAt
    paymentMethod
    cardBrand
    lastFourDigits
    appliedCredit
    refunds {
      id
      amount
      donation
      processingFee
      taxFee
      kind
      reason
      issuedBy
      lines {
        quantity
        amount
        ticketTypeName
        eventName
        seatLabels
      }
    }
  }
  ${EVENT_SUMMARY_FRAGMENT}
`;

const ORDERS_QUERY = gql`
  query Orders($after: String, $before: String, $first: Int, $last: Int, $filter: OrderSearchArguments, $sort: [OrderingArgument!]) {
    orders(after: $after, before: $before, first: $first, last: $last, filter: $filter, sort: $sort) {
      edges {
        cursor
        node {
          ...OrderSummaryFields
        }
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
    }
  }
  ${ORDER_SUMMARY_FIELDS}
  ${CONNECTION_METADATA_FIELDS}
`;

const ORDER_QUERY = gql`
  query Order($id: Int!) {
    order(id: $id) {
      ...OrderDetailFields
    }
  }
  ${ORDER_DETAIL_FIELDS}
`;

const RESEND_ORDER_CONFIRMATION_MUTATION = gql`
  mutation ResendOrderConfirmation($orderId: Int!) {
    resendOrderConfirmation(input: { orderId: $orderId }) {
      errors {
        message
        path
      }
      success
    }
  }
`;

export const UPDATE_ORDER_CUSTOMER_MUTATION = gql`
  mutation UpdateOrderCustomer($orderId: Int!, $updateOrderCustomerInput: UpdateOrderCustomerInput!) {
    updateOrderCustomer(input: { orderId: $orderId, updateOrderCustomerInput: $updateOrderCustomerInput }) {
      errors {
        message
        path
      }
    }
  }
`;

export const fetchOrders = async (
  client: ApolloClient<Query>,
  {
    pagination,
    filter,
    sort,
  }: { pagination?: ConnectionArguments; filter?: OrderSearchArguments; sort?: Ordering.Ordering<'created_at'>[] },
): Promise<GqlConnection.GqlConnection<Order.Order>> => {
  const { data } = await client.query({
    query: ORDERS_QUERY,
    fetchPolicy: 'network-only',
    variables: {
      ...(pagination ?? {}),
      filter: filter ?? {},
      sort: sort ?? [],
    },
  });

  return GqlConnection.fromGql(data.orders, Order.fromGql);
};

export const fetchOrder = async (client: ApolloClient<Query>, id: number): Promise<OrderDetail.OrderDetail | undefined> => {
  const { data } = await client.query({
    query: ORDER_QUERY,
    fetchPolicy: 'network-only',
    variables: { id },
  });

  return data.order ? OrderDetail.fromGql(data.order) : undefined;
};

// `manuallyCompleteOrder` and `makeTicketUnclaimable` are NOT here: they are staff-only,
// cross-account interventions and now live on the internal schema. See api/Internal/Order/Api.ts.

export const resendOrderConfirmation = async (
  client: ApolloClient<Query>,
  orderId: number,
): Promise<{ errors: VPError.VPError[]; success: boolean }> => {
  const { data } = await client.mutate({
    mutation: RESEND_ORDER_CONFIRMATION_MUTATION,
    variables: { orderId },
    fetchPolicy: 'network-only',
  });

  const result = data?.resendOrderConfirmation;

  if (!result?.success) {
    return { errors: VPError.fromGqlCollection(result?.errors ?? []), success: false };
  }

  return { errors: [], success: true };
};

export const updateOrderCustomer = async (
  client: ApolloClient<Query>,
  orderId: number,
  customer: UpdateOrderCustomerInput,
): Promise<{ errors: VPError.VPError[]; success: boolean }> => {
  const { data } = await client.mutate({
    mutation: UPDATE_ORDER_CUSTOMER_MUTATION,
    variables: { orderId, updateOrderCustomerInput: customer },
    fetchPolicy: 'network-only',
  });

  const errors = VPError.fromGqlCollection(data?.updateOrderCustomer?.errors ?? []);

  return { errors, success: errors.length === 0 };
};
